'use client';
import { motion, useReducedMotion } from 'motion/react';
import { useEffect, useState, type ReactNode } from 'react';

function useIsHydrated() {
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional post-mount flip to opt into client-only motion variant
    setHydrated(true);
  }, []);
  return hydrated;
}

/**
 * Springy in-view entrance (motion.dev style): fades in while scaling
 * 0.94 → 1 with a gentle spring the first time the element scrolls into view.
 * Use for cards and tiles; `delay` staggers siblings.
 *
 * SSR-safe (plain div until hydration) and inert for reduced-motion users.
 */
export function PopIn({ children, delay = 0, className }: { children: ReactNode; delay?: number; className?: string }) {
  const hydrated = useIsHydrated();
  const reduce = useReducedMotion();

  if (!hydrated || reduce) return <div className={className}>{children}</div>;

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, scale: 0.94, y: 16 }}
      whileInView={{ opacity: 1, scale: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ type: 'spring', stiffness: 130, damping: 17, delay }}
    >
      {children}
    </motion.div>
  );
}
